import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import backendApi from "../api/backendApi";
import { useAuth } from "../api/AuthContext";
import AnalyticsBreakdownTable from "../components/adminAnalytics/AnalyticsBreakdownTable";
import AnalyticsFilterBar from "../components/adminAnalytics/AnalyticsFilterBar";
import AnalyticsKpiCards from "../components/adminAnalytics/AnalyticsKpiCards";
import AnalyticsPdfExportAction from "../components/adminAnalytics/AnalyticsPdfExportAction";
import AnalyticsPrimaryChart from "../components/adminAnalytics/AnalyticsPrimaryChart";
import { protectedTheme } from "../components/ui/protectedTheme";
import {
  ANALYTICS_CATEGORIES,
  MONTH_OPTIONS,
  averageValues,
  buildYearOptions,
  clamp,
  formatCategoryName,
  formatCurrency,
  formatMuqamiName,
  getKualaLumpurDateParts,
  sumValues,
} from "../components/adminAnalytics/analyticsUtils";

const DEFAULT_CATEGORY = "KHUDDAM_IJTIMA";
const DEFAULT_VIEW = "trend";
const YEAR_START = 2025;
const MUQAMI_VIEW_OPTIONS = [
  { value: "trend", label: "Trend" },
  { value: "compare", label: "Category Comparison" },
];

const CHART_COLORS = {
  KHUDDAM_IJTIMA: "#111827",
  KHUDDAM_MEMBERSHIP: "#1d4ed8",
  ATHFAL_IJTIMA: "#0f766e",
  ATHFAL_MEMBERSHIP: "#0891b2",
  HALL: "#7c3aed",
  LOCAL_FUND: "#2563eb",
  KHIDMAT_KHALAQ: "#c2410c",
  TRX_FEE: "#475569",
  TAX: "#be123c",
};

function parseInteger(value) {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildSearchParamsFromFilters(filters) {
  const params = new URLSearchParams();
  params.set("year", String(filters.year));
  params.set("category", filters.category);
  params.set("view", filters.view);
  params.set("fromMonth", String(filters.fromMonth));
  params.set("toMonth", String(filters.toMonth));
  params.set("includeLocalFund", filters.includeLocalFund ? "1" : "0");
  return params;
}

function normalizeFilters(searchParams) {
  const { currentYear, currentMonth } = getKualaLumpurDateParts();
  const yearOptions = buildYearOptions(YEAR_START, currentYear);
  const yearParam = parseInteger(searchParams.get("year"));
  const year = yearOptions.includes(yearParam) ? yearParam : currentYear;
  const maxMonth = year === currentYear ? currentMonth : 12;

  const viewParam = searchParams.get("view");
  const view = MUQAMI_VIEW_OPTIONS.some((option) => option.value === viewParam)
    ? viewParam
    : DEFAULT_VIEW;

  const categoryParam = searchParams.get("category");
  const category = ANALYTICS_CATEGORIES.includes(categoryParam)
    ? categoryParam
    : DEFAULT_CATEGORY;

  const fromMonth = clamp(parseInteger(searchParams.get("fromMonth")) ?? 1, 1, maxMonth);
  const toMonth = clamp(
    parseInteger(searchParams.get("toMonth")) ?? maxMonth,
    fromMonth,
    maxMonth
  );

  return {
    year,
    category,
    view,
    fromMonth,
    toMonth,
    includeLocalFund: searchParams.get("includeLocalFund") === "1",
    maxMonth,
    currentYear,
    currentMonth,
  };
}

function getMonthRange(fromMonth, toMonth) {
  return MONTH_OPTIONS.filter(
    (month) => month.value >= fromMonth && month.value <= toMonth
  );
}

function getSeriesMonths(series) {
  if (Array.isArray(series?.months) && series.months.length) {
    return series.months.map((entry, index) => ({
      paymentMonth: entry.paymentMonth || "",
      month: Number(entry.month ?? index + 1),
      label:
        entry.label ||
        MONTH_OPTIONS.find((month) => month.value === Number(entry.month))
          ?.label ||
        `M${index + 1}`,
      totalAmount: Number(entry.totalAmount ?? 0),
      transactionCount: Number(entry.transactionCount ?? 0),
    }));
  }

  return MONTH_OPTIONS.map((month, index) => ({
    paymentMonth: `${series?.year ?? ""}-${String(month.value).padStart(2, "0")}`,
    month: month.value,
    label: month.label,
    totalAmount: Number(series?.values?.[index] ?? 0),
    transactionCount: 0,
  }));
}

function sliceSeriesPoints(series, fromMonth, toMonth) {
  return getSeriesMonths(series).filter(
    (month) => month.month >= fromMonth && month.month <= toMonth
  );
}

function getTrendRows(points) {
  return points.map((point) => ({
    month: point.label,
    paymentMonth: point.paymentMonth,
    transactionCount: point.transactionCount,
    value: point.totalAmount,
  }));
}

function formatPaymentMonth(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function formatRange(year, fromMonth, toMonth) {
  const start = formatPaymentMonth(year, fromMonth);
  const end = formatPaymentMonth(year, toMonth);
  return start === end ? start : `${start} to ${end}`;
}

function getFilenameFromContentDisposition(contentDisposition) {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || "");
  return match?.[1] || "";
}

async function getDownloadErrorMessage(error) {
  const fallback = error?.message || "Failed to export Muqami analytics PDF.";
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }

  return data?.message || fallback;
}

const MuqamiDashboardPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => normalizeFilters(searchParams), [searchParams]);
  const yearOptions = useMemo(
    () => buildYearOptions(YEAR_START, filters.currentYear),
    [filters.currentYear]
  );

  const [trendState, setTrendState] = useState({
    loading: false,
    error: "",
    primary: null,
  });
  const [breakdownState, setBreakdownState] = useState({
    loading: false,
    error: "",
    data: null,
  });
  const [pdfExportState, setPdfExportState] = useState({
    loading: false,
    error: "",
  });

  useEffect(() => {
    const canonicalParams = buildSearchParamsFromFilters(filters).toString();
    if (canonicalParams !== searchParams.toString()) {
      setSearchParams(canonicalParams, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };

    if (key === "year") {
      const maxMonth = value === filters.currentYear ? filters.currentMonth : 12;
      next.fromMonth = clamp(next.fromMonth, 1, maxMonth);
      next.toMonth = clamp(next.toMonth, next.fromMonth, maxMonth);
    }

    if (key === "fromMonth") {
      next.toMonth = Math.max(value, next.toMonth);
    }

    if (key === "toMonth") {
      next.fromMonth = Math.min(value, next.fromMonth);
    }

    setSearchParams(buildSearchParamsFromFilters(next));
  };

  const handleReset = () => {
    const { currentYear, currentMonth } = getKualaLumpurDateParts();
    setSearchParams(
      buildSearchParamsFromFilters({
        year: currentYear,
        category: DEFAULT_CATEGORY,
        view: DEFAULT_VIEW,
        fromMonth: 1,
        toMonth: currentMonth,
        includeLocalFund: false,
      })
    );
  };

  const selectedMonths = useMemo(
    () => getMonthRange(filters.fromMonth, filters.toMonth),
    [filters.fromMonth, filters.toMonth]
  );
  const rangeLabel = useMemo(
    () => formatRange(filters.year, filters.fromMonth, filters.toMonth),
    [filters.year, filters.fromMonth, filters.toMonth]
  );
  const muqamiLabel = formatMuqamiName(user?.muqami || "");

  useEffect(() => {
    if (filters.view !== "trend") return;

    let ignore = false;

    const loadTrend = async () => {
      setTrendState({ loading: true, error: "", primary: null });
      try {
        const response = await backendApi.get(
          "/muqami/analytics/revenue/monthly",
          {
            params: {
              category: filters.category,
              year: filters.year,
            },
          }
        );
        if (!ignore) {
          setTrendState({ loading: false, error: "", primary: response.data });
        }
      } catch (error) {
        if (!ignore) {
          setTrendState({
            loading: false,
            error:
              error?.response?.data?.message ||
              error.message ||
              "Failed to load Muqami analytics trend",
            primary: null,
          });
        }
      }
    };

    loadTrend();
    return () => {
      ignore = true;
    };
  }, [filters.category, filters.view, filters.year]);

  useEffect(() => {
    if (filters.view !== "compare") return;

    let ignore = false;

    const loadBreakdown = async () => {
      setBreakdownState({ loading: true, error: "", data: null });
      try {
        const response = await backendApi.get(
          "/muqami/analytics/revenue/breakdown",
          {
            params: {
              year: filters.year,
              fromMonth: filters.fromMonth,
              toMonth: filters.toMonth,
            },
          }
        );
        if (!ignore) {
          setBreakdownState({ loading: false, error: "", data: response.data });
        }
      } catch (error) {
        if (!ignore) {
          setBreakdownState({
            loading: false,
            error:
              error?.response?.data?.message ||
              error.message ||
              "Failed to load Muqami analytics breakdown",
            data: null,
          });
        }
      }
    };

    loadBreakdown();
    return () => {
      ignore = true;
    };
  }, [filters.fromMonth, filters.toMonth, filters.view, filters.year]);

  const handleExportPdf = async () => {
    if (pdfExportState.loading) return;

    setPdfExportState({ loading: true, error: "" });

    try {
      const response = await backendApi.get(
        "/muqami/analytics/revenue/report.pdf",
        {
          params: {
            year: filters.year,
            fromMonth: filters.fromMonth,
            toMonth: filters.toMonth,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fallbackFilename = `muqami-analytics-${rangeLabel.replace(
        /\s+to\s+/,
        "-to-"
      )}.pdf`;

      link.href = url;
      link.download =
        getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
        fallbackFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPdfExportState({ loading: false, error: "" });
    } catch (error) {
      const message = await getDownloadErrorMessage(error);
      setPdfExportState({ loading: false, error: message });
    }
  };

  const trendViewModel = useMemo(() => {
    if (!trendState.primary) return null;

    const points = sliceSeriesPoints(
      trendState.primary,
      filters.fromMonth,
      filters.toMonth
    );
    const labels = points.map((point) => point.label);
    const values = points.map((point) => point.totalAmount);
    const total = sumValues(values);
    const average = averageValues(values);
    const peakValue = Math.max(...values, 0);
    const peakIndex = values.findIndex((value) => value === peakValue);

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: `${formatCategoryName(filters.category)} ${filters.year}`,
            data: values,
            borderColor: CHART_COLORS[filters.category] || "#064e3b",
            backgroundColor: "rgba(6, 78, 59, 0.12)",
            fill: true,
            tension: 0.32,
          },
        ],
      },
      rows: getTrendRows(points),
      cards: [
        {
          label: "Selected Range Total",
          value: total,
          tone: "currency",
          helpText: rangeLabel,
        },
        {
          label: "Average Per Month",
          value: average,
          tone: "currency",
          helpText: `${labels.length} months included`,
        },
        {
          label: "Peak Month",
          value: peakValue,
          tone: "currency",
          helpText:
            peakIndex >= 0 && peakValue > 0
              ? points[peakIndex]?.paymentMonth || labels[peakIndex]
              : "No collected amount",
        },
        {
          label: "Category",
          value: formatCategoryName(filters.category),
          helpText: `Scoped to ${muqamiLabel}`,
        },
      ],
    };
  }, [
    filters.category,
    filters.fromMonth,
    filters.toMonth,
    filters.year,
    muqamiLabel,
    rangeLabel,
    trendState.primary,
  ]);

  const compareViewModel = useMemo(() => {
    if (!breakdownState.data) return null;

    const visibleItems = (breakdownState.data.items ?? [])
      .filter((item) => filters.includeLocalFund || item.category !== "LOCAL_FUND")
      .map((item) => ({
        ...item,
        totalAmount: Number(item.totalAmount ?? 0),
        totalTransactions: Number(item.totalTransactions ?? 0),
        averageAmount: Number(item.averageAmount ?? 0),
      }))
      .sort((left, right) => right.totalAmount - left.totalAmount);

    const totalVisibleAmount = visibleItems.reduce(
      (sum, item) => sum + item.totalAmount,
      0
    );
    const totalVisibleTransactions = visibleItems.reduce(
      (sum, item) => sum + item.totalTransactions,
      0
    );
    const topCategory = visibleItems[0];
    const selectedCategoryItem = visibleItems.find(
      (item) => item.category === filters.category
    );

    const rows = visibleItems.map((item) => ({
      ...item,
      share: totalVisibleAmount
        ? (item.totalAmount / totalVisibleAmount) * 100
        : 0,
    }));

    return {
      chartData: rows.length
        ? {
            labels: rows.map((row) => row.label),
            datasets: [
              {
                data: rows.map((row) => row.totalAmount),
                backgroundColor: rows.map((row) =>
                  row.category === filters.category
                    ? "#bef264"
                    : CHART_COLORS[row.category] || "#064e3b"
                ),
                borderRadius: 10,
              },
            ],
          }
        : null,
      rows,
      cards: [
        {
          label: "Selected Range Total",
          value: totalVisibleAmount,
          tone: "currency",
          helpText: `${selectedMonths[0]?.label} to ${
            selectedMonths[selectedMonths.length - 1]?.label
          } ${filters.year}`,
        },
        {
          label: "Selected Category",
          value: selectedCategoryItem?.totalAmount ?? 0,
          tone: "currency",
          helpText: selectedCategoryItem
            ? selectedCategoryItem.label
            : `${formatCategoryName(filters.category)} is not visible in this view`,
        },
        {
          label: "Top Category",
          value: topCategory?.label ?? "-",
          helpText: topCategory
            ? formatCurrency(topCategory.totalAmount)
            : "No analytics data",
        },
        {
          label: "Transactions",
          value: totalVisibleTransactions.toLocaleString("en-MY"),
          helpText:
            totalVisibleTransactions > 0
              ? `Avg ${formatCurrency(
                  totalVisibleAmount / totalVisibleTransactions
                )} per transaction`
              : "No successful transactions",
        },
      ],
    };
  }, [
    breakdownState.data,
    filters.category,
    filters.includeLocalFund,
    filters.year,
    selectedMonths,
  ]);

  const activeViewModel =
    filters.view === "trend" ? trendViewModel : compareViewModel;
  const chartLoading =
    filters.view === "trend" ? trendState.loading : breakdownState.loading;
  const chartError =
    filters.view === "trend" ? trendState.error : breakdownState.error;
  const kpiCards =
    activeViewModel?.cards ??
    (filters.view === "trend"
      ? [
          { label: "Selected Range Total" },
          { label: "Average Per Month" },
          { label: "Peak Month" },
          { label: "Category" },
        ]
      : [
          { label: "Selected Range Total" },
          { label: "Selected Category" },
          { label: "Top Category" },
          { label: "Transactions" },
        ]);

  return (
    <div className={protectedTheme.page}>
      <div className={protectedTheme.shell}>
        <div className="rounded-3xl bg-emerald-950 p-6 text-white shadow-xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-lime-200">
            Muqami Reporting
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-tight">
            Muqami Dashboard
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-emerald-100">
            Analytics for {muqamiLabel}. Your Muqami scope is derived on the
            server from your authenticated profile, so this page never requests
            or exposes a global Muqami selector.
          </p>
        </div>

        <AnalyticsFilterBar
          filters={{
            ...filters,
            muqami: user?.muqami || "ALL",
            compareYear: "",
          }}
          yearOptions={yearOptions}
          maxMonth={filters.maxMonth}
          viewOptions={MUQAMI_VIEW_OPTIONS}
          showMuqamiFilter={false}
          allowCompareYear={false}
          onChange={handleFilterChange}
          onReset={handleReset}
        />

        <AnalyticsPdfExportAction
          rangeLabel={rangeLabel}
          scopeLabel={muqamiLabel}
          loading={pdfExportState.loading}
          error={pdfExportState.error}
          onExport={handleExportPdf}
        />

        <AnalyticsKpiCards
          items={kpiCards}
          loading={chartLoading}
          error={chartError}
        />

        <AnalyticsPrimaryChart
          view={filters.view}
          chartData={activeViewModel?.chartData ?? null}
          loading={chartLoading}
          error={chartError}
          title={
            filters.view === "trend"
              ? `${formatCategoryName(filters.category)} trend`
              : "Category comparison"
          }
          subtitle={
            filters.view === "trend"
              ? `Monthly totals by payment month for ${muqamiLabel} in ${filters.year}.`
              : `Totals across payment months ${selectedMonths[0]?.label} to ${
                  selectedMonths[selectedMonths.length - 1]?.label
                } ${filters.year} for ${muqamiLabel}.`
          }
          emptyMessage="No Muqami analytics data for the selected filters."
        />

        <AnalyticsBreakdownTable
          view={filters.view}
          rows={activeViewModel?.rows ?? []}
          loading={chartLoading}
          error={chartError}
          compareYear=""
        />
      </div>
    </div>
  );
};

export default MuqamiDashboardPage;
